import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import LazLogo from "@/components/LazLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      const fallback = res.user?.role === "admin" ? "/admin" : "/dashboard";
      navigate(location.state?.from?.pathname || fallback, { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen flex bg-stone-50">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <LazLogo size={48} className="mb-10" />

          <h1 className="font-serif text-4xl text-stone-900 tracking-tight">Welcome back.</h1>
          <p className="text-stone-500 mt-2">Sign in to continue your growth.</p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <Label htmlFor="email" className="text-stone-700">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-xl border-stone-200 bg-white h-11 focus-visible:ring-amber-700 focus-visible:ring-offset-0 focus-visible:border-amber-700"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-stone-700">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-xl border-stone-200 bg-white h-11 focus-visible:ring-amber-700 focus-visible:ring-offset-0 focus-visible:border-amber-700"
                placeholder="At least 6 characters"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-xl"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50 shadow-sm hover:shadow-md transition-all"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 text-sm text-stone-500">
            New here?{" "}
            <Link to="/apply" data-testid="login-register-link" className="text-amber-700 hover:underline">
              Start your application
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block flex-1 relative">
        <img
          src="https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg"
          alt="Coaches collaborating"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-stone-900/40 via-stone-900/10 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-stone-50">
          <div className="font-serif italic text-3xl leading-tight max-w-md">
            "Growth is rarely a solo sport. Find your guide."
          </div>
        </div>
      </div>
    </div>
  );
}
